/**
 * Ambient + celebration effects for the How It Works tour.
 *
 * - FloatingParticles: slow drifting dust motes behind every slide.
 * - ConfettiBurst: geometric confetti rain for the finale slide.
 *
 * Pure CSS animations (MUI keyframes) — no timers, no canvas. Particle and
 * confetti geometry is derived deterministically from the piece index so the
 * render is stable across re-renders.
 */
import { Box } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';

const drift = keyframes`
  0%   { transform: translate3d(0, 0, 0) scale(1); opacity: 0; }
  10%  { opacity: 0.7; }
  90%  { opacity: 0.7; }
  100% { transform: translate3d(4vw, -105vh, 0) scale(1.25); opacity: 0; }
`;

const confettiFall = keyframes`
  0%   { transform: translate3d(0, -12vh, 0) rotateZ(0deg) rotateY(0deg); opacity: 0; }
  8%   { opacity: 1; }
  100% { transform: translate3d(var(--sway), 110vh, 0) rotateZ(720deg) rotateY(540deg); opacity: 0.9; }
`;

/** Deterministic pseudo-random in [0,1) from an integer seed. */
const rand = (seed: number): number => {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

const PARTICLE_COUNT = 18;

export const FloatingParticles = () => {
  const theme = useTheme();
  return (
    <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {Array.from({ length: PARTICLE_COUNT }, (_, i) => {
        const size = 3 + rand(i) * 6;
        return (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${rand(i + 40) * 100}%`,
              top: `${55 + rand(i + 80) * 55}%`,
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: alpha(
                i % 3 === 0 ? theme.schedule.maintenance : theme.palette.common.white,
                i % 3 === 0 ? 0.5 : 0.35,
              ),
              animation: `${drift} ${18 + rand(i + 7) * 22}s linear infinite`,
              animationDelay: `${-rand(i + 13) * 30}s`,
              willChange: 'transform, opacity',
            }}
          />
        );
      })}
    </Box>
  );
};

const CONFETTI_COUNT = 42;

export const ConfettiBurst = () => {
  const theme = useTheme();
  const colors = [
    theme.palette.primary.main,
    theme.schedule.tentative,
    theme.schedule.maintenance,
    theme.schedule.machineTime,
    theme.palette.common.white,
  ];
  return (
    <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {Array.from({ length: CONFETTI_COUNT }, (_, i) => {
        const w = 6 + rand(i + 3) * 8;
        const round = i % 4 === 0;
        return (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${rand(i + 21) * 100}%`,
              top: 0,
              width: w,
              height: round ? w : w * 1.8,
              borderRadius: round ? '50%' : '2px',
              backgroundColor: alpha(colors[i % colors.length], 0.9),
              '--sway': `${(rand(i + 55) - 0.5) * 30}vw`,
              animation: `${confettiFall} ${4.5 + rand(i + 9) * 4}s ease-in infinite`,
              animationDelay: `${rand(i + 31) * 3.5}s`,
              willChange: 'transform, opacity',
            }}
          />
        );
      })}
    </Box>
  );
};
